<template>
  <div class="builder-page">
    <div class="wrap">
      <header class="builder-head">
        <a class="brand" href="/" aria-label="Foodrazor">
          <img src="../../../assets/Foodrazor-logo.svg" alt="Foodrazor">
        </a>
        <div class="eyebrow"><span class="dot"></span>Build your subscription</div>
        <h1 class="h-display">Configure your plan, <span class="hl">see your price.</span></h1>
        <p class="lede">Pick a plan and choose how you want to get started. Your cost updates live on the right.</p>

        <div class="billing-toggle" role="tablist" aria-label="Billing period">
          <button
            v-for="b in copy.billing"
            :key="b.id"
            class="billing-opt"
            :class="{ active: b.id === state.billing }"
            role="tab"
            :aria-selected="String(b.id === state.billing)"
            type="button"
            @click="setBilling(b.id)"
          >
            {{ b.label }}
            <span v-if="b.id === 'year' && minSaving > 0" class="save">Save {{ minSaving }}%</span>
          </button>
        </div>
      </header>

      <p v-if="loading" class="state-note">Loading pricing…</p>
      <p v-else-if="loadError" class="state-note error">{{ loadError }}</p>

      <div v-else class="builder-grid">

        <!-- ============ LEFT: STEPS ============ -->
        <div class="steps">

          <!-- STEP 1 — PLAN -->
          <section class="step">
            <div class="step-head">
              <span class="step-num">1</span>
              <div>
                <h2 class="step-title">{{ copy.planStep.title }}</h2>
                <p class="step-sub">{{ copy.planStep.subtitle }}</p>
              </div>
            </div>

            <div class="opt-grid">
              <div
                v-for="p in plans"
                :key="p.id"
                class="opt"
                :class="{ selected: p.id === state.plan }"
                role="button"
                tabindex="0"
                @click="setPlan(p.id)"
                @keydown.enter.prevent="setPlan(p.id)"
                @keydown.space.prevent="setPlan(p.id)"
              >
                <span v-if="p.promoted" class="badge">{{ copy.badges.promoted }}</span>
                <span class="tick"><tick-icon /></span>
                <p class="o-name">{{ p.name }}</p>
                <div class="o-price">
                  <span class="amt">{{ money0(monthlyOf(p, state.billing)) }}</span>
                  <span class="per">/mo</span>
                </div>
                <div class="o-billed">{{ currentBilling.billedNote }}</div>
                <p class="o-desc">{{ describe(p) }}</p>
              </div>
            </div>
          </section>

          <!-- STEP 2 — ONBOARDING -->
          <section class="step" v-if="onboarding?.length">
            <div class="step-head">
              <span class="step-num">2</span>
              <div>
                <h2 class="step-title">{{ copy.onboardStep.title }}</h2>
                <p class="step-sub">{{ copy.onboardStep.subtitle }}</p>
              </div>
            </div>

            <div class="opt-rows">
              <div
                v-for="o in onboarding"
                :key="o.id"
                class="opt-row"
                :class="{ selected: o.id === state.onboard }"
                role="button"
                tabindex="0"
                @click="setOnboard(o.id)"
                @keydown.enter.prevent="setOnboard(o.id)"
                @keydown.space.prevent="setOnboard(o.id)"
              >
                <span class="tick"><tick-icon /></span>
                <div class="r-body">
                  <p class="r-name">{{ o.name }}</p>
                  <p class="r-desc">{{ describe(o) }}</p>
                </div>
                <div class="r-price">
                  {{ money0(o.amount / 100) }}
                  <span class="once">one-time</span>
                </div>
              </div>
            </div>
          </section>

        </div>

        <!-- ============ RIGHT: SUMMARY ============ -->
        <aside class="summary">
          <div class="summary-card">
            <div class="summary-head">
              <h3>Your subscription</h3>
              <p>{{ summaryPlanLine }}</p>
            </div>

            <div class="summary-body">
              <div class="sec-label">Monthly</div>
              <div class="li">
                <span class="li-name">
                  {{ selectedPlan.name }} plan
                  <span class="li-sub">{{ copy.planStep.unitLabel }} · {{ planUnitNote }}</span>
                </span>
                <span class="li-amt">{{ money0(monthlyRecurring) }}/mo</span>
              </div>

              <div class="summary-divider"></div>

              <div class="subtotal">
                <span class="st-name">Monthly subtotal</span>
                <span class="st-amt">{{ money(monthlyRecurring) }}/mo</span>
              </div>

              <template v-if="oneTimeTotal > 0">
                <div class="sec-label">One-time</div>
                <div class="li">
                  <span class="li-name">
                    {{ selectedOnboarding.name }}
                    <span class="li-sub">charged once at signup</span>
                  </span>
                  <span class="li-amt">{{ money0(oneTimeTotal) }}</span>
                </div>
              </template>

              <div class="total-block">
                <div class="total-row">
                  <div class="t-label">
                    Recurring total
                    <small>plus applicable taxes</small>
                  </div>
                  <div class="t-amt">
                    {{ money(headlineAmount) }}<span class="per"> {{ headlinePer }}</span>
                  </div>
                </div>

                <div v-if="!isMonthly && annualSaving > 0" class="annual-note">
                  That's {{ money(monthlyRecurring) }}/mo billed annually ·
                  <span class="save">save {{ money0(annualSaving) }}/yr</span> on your plan.
                </div>

                <div class="paynow-row">
                  <div class="pn-label">
                    Due today
                  </div>
                  <div class="pn-amt">{{ money(payNowTotal) }}</div>
                </div>
              </div>
            </div>

            <div class="summary-foot">
              <button
                class="btn btn-primary"
                type="button"
                :disabled="!cart || submitting"
                @click="onCheckout"
              >
                {{ submitting ? 'Redirecting…' : 'Continue to checkout' }}
                <svg class="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
                     stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>

              <p v-if="checkoutError" class="state-note error small">{{ checkoutError }}</p>

              <div class="trust">
                <span><span class="check"><tick-icon /></span> Cancel or change plan anytime</span>
                <span><span class="check"><tick-icon /></span> Secure checkout via Stripe</span>
              </div>
            </div>
          </div>
        </aside>

      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

// Presentation copy that is not priced lives here; every price comes from the API.
const COPY = {
  planStep: { title: 'Choose your plan', subtitle: 'Priced per outlet.', unitLabel: 'per outlet' },
  onboardStep: {
    title: 'How do you want to get started?',
    subtitle: 'Optional one-time setup help — charged once, not monthly.',
  },
  billing: [
    { id: 'month', label: 'Monthly', billedNote: '' },
    { id: 'year', label: 'Yearly', billedNote: 'billed annually' },
  ],
  // Plan and onboarding defaults are not listed here — they come from the
  // `promoted` flag on the API rows (see applyDefaults).
  defaults: { billing: 'month' },
  descriptions: {
    lite: 'Digitize invoices, manage suppliers, track spending in one outlet.',
    plus: 'Adds invoice automations, inventory, and accounting integrations.',
    pro: 'Full recipe costing, ingredient tracking, and menu margins.',
    setup_call: 'A 60–90 minute guided walkthrough of your subscription, so you start with confidence.',
    success_onboarding: 'We do it with you — catalogs, recipes, inventory and more, set up end to end.',
  },
  badges: { promoted: 'Most popular' },
};

const TickIcon = {
  name: 'TickIcon',
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '3.4',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
      },
    }, [h('polyline', { attrs: { points: '20 6 9 17 4 12' } })]);
  },
};

export default {
  name: 'SubscriptionBuilder',

  components: { TickIcon },

  data() {
    return {
      copy: COPY,
      plans: [],       // [{ id, name, description, promoted, prices: { month: { key, amount }, year: {...} } }]
      onboarding: [],  // [{ id, name, description, amount }]
      state: {
        plan: null,
        billing: COPY.defaults.billing,
        onboard: null,
      },
      loading: true,
      loadError: '',
      submitting: false,
      checkoutError: '',
    };
  },

  computed: {
    selectedPlan() {
      return this.plans.find(p => p.id === this.state.plan) || this.plans[0] || null;
    },

    selectedOnboarding() {
      return this.onboarding.find(o => o.id === this.state.onboard) || null;
    },

    currentBilling() {
      return this.copy.billing.find(b => b.id === this.state.billing) || this.copy.billing[0];
    },

    isMonthly() {
      return this.state.billing === 'month';
    },

    // smallest yearly saving across plans, derived from the API — never hardcoded
    minSaving() {
      const savings = this.plans.map(p => this.savingPercent(p)).filter(n => n > 0);
      return savings.length ? Math.min(...savings) : 0;
    },

    monthlyRecurring() {
      if (!this.selectedPlan) return 0;
      return this.monthlyOf(this.selectedPlan, this.state.billing);
    },

    oneTimeTotal() {
      return this.selectedOnboarding ? this.selectedOnboarding.amount / 100 : 0;
    },

    cycleAmount() {
      if (!this.selectedPlan) return 0;
      return this.selectedPlan.prices[this.state.billing].amount / 100;
    },

    headlineAmount() {
      return this.isMonthly ? this.monthlyRecurring : this.cycleAmount;
    },

    headlinePer() {
      return this.isMonthly ? '/month' : '/year';
    },

    payNowTotal() {
      return this.headlineAmount + this.oneTimeTotal;
    },

    annualSaving() {
      const p = this.selectedPlan;
      if (!p || !p.prices.month || !p.prices.year) return 0;
      return Math.max(0, (p.prices.month.amount * 12 - p.prices.year.amount) / 100);
    },

    planUnitNote() {
      if (!this.selectedPlan) return '';
      return this.isMonthly
        ? 'monthly'
        : `${this.money0(this.selectedPlan.prices.year.amount / 100)}/yr`;
    },

    summaryPlanLine() {
      if (!this.selectedPlan) return '';
      return `${this.selectedPlan.name} plan · billed ${this.currentBilling.label.toLowerCase()}`;
    },

    cart() {
      const p = this.selectedPlan;
      const o = this.selectedOnboarding;
      if (!p) return null;

      // both are required — no cart until an onboarding option is chosen
      // unless user is buying after having canceled subscription
      // in which case onboarding prices are not returned from API and are hidden
      if (!o && this.onboarding?.length) return null;

      return {
        plan: { key: p.prices[this.state.billing].key },
        ...!!o && { onboarding: { key: o.id }},
      };
    },
  },

  created() {
    this.loadConfig();
  },

  methods: {
    ...mapActions('stripe', [
      'venueFetchStripePrices',
      'venueCreateCheckoutSession',
    ]),

    /**
     * GET the subscription config (plans + onboarding options, prices in cents).
     */
    async fetchSubscriptionConfig() {
      const { data } = await this.venueFetchStripePrices({});
      return data;
    },

    /**
     * POST the cart and return the Stripe Checkout session ({ url }).
     */
    async postCheckoutSession(cart) {
      const { data } = await this.venueCreateCheckoutSession({ data: cart });
      return (data && data.data) || data;
    },

    // =========================================================================
    // LOADING / INIT
    // =========================================================================

    async loadConfig() {
      this.loading = true;
      this.loadError = '';

      try {
        const config = await this.fetchSubscriptionConfig();
        this.parseConfig(config);

        if (!this.plans.length) {
          throw new Error('No priced plans returned by the pricing API.');
        }

        this.applyDefaults();
      } catch (error) {
        this.loadError = 'Pricing configuration could not be loaded.';
        // eslint-disable-next-line no-console
        console.error('[builder] init failed', error);
      } finally {
        this.loading = false;
      }
    },

    parseConfig(config) {
      const rows = (config && config.data) || [];
      const index = {};
      const plans = [];
      const onboarding = [];

      rows.forEach((row) => {
        if (row.type === 'plan') {
          const id = row.key.replace(/_(month|year)$/, '');
          if (!index[id]) {
            index[id] = {
              id,
              name: row.name,
              description: row.description,
              promoted: !!row.promoted,
              prices: {},
            };
            plans.push(index[id]);
          }
          if (row.promoted) index[id].promoted = true;
          index[id].prices[row.interval] = { key: row.key, amount: row.amount };
        } else if (row.type === 'onboarding') {
          onboarding.push({
            id: row.key,
            name: row.name,
            description: row.description,
            promoted: !!row.promoted,
            amount: row.amount,
          });
        }
      });

      // keep only plans priced for every billing period we offer
      this.plans = plans.filter(p => this.copy.billing.every(
        b => p.prices[b.id] && typeof p.prices[b.id].amount === 'number',
      ));
      this.onboarding = onboarding.filter(o => typeof o.amount === 'number');
    },

    /**
     * Selection defaults are driven entirely by the API: the row flagged
     * `promoted` wins, otherwise the first option in the section. No API keys
     * are hardcoded here.
     */
    applyDefaults() {
      const defaultPlan = this.plans.find(p => p.promoted) || this.plans[0];
      this.state.plan = defaultPlan ? defaultPlan.id : null;

      const defaultOnboard = this.onboarding.find(o => o.promoted) || this.onboarding[0];
      this.state.onboard = defaultOnboard ? defaultOnboard.id : null;

      const defaultBilling = this.copy.billing.find(b => b.id === this.copy.defaults.billing);
      this.state.billing = defaultBilling ? defaultBilling.id : this.copy.billing[0].id;
    },

    // =========================================================================
    // SELECTION
    // =========================================================================

    setPlan(id) {
      this.state.plan = id;
    },

    setBilling(id) {
      this.state.billing = id;
    },

    // Onboarding is a required choice — selecting is the only action, there is
    // no deselect. Clicking the already-selected row is a no-op.
    setOnboard(id) {
      this.state.onboard = id;
    },

    // =========================================================================
    // CHECKOUT
    // =========================================================================

    async onCheckout() {
      if (!this.cart || this.submitting) return;

      this.submitting = true;
      this.checkoutError = '';

      try {
        const session = await this.postCheckoutSession(this.cart);

        if (!session || !session.url) {
          throw new Error('No Stripe Checkout URL returned.');
        }

        window.location.href = session.url;
      } catch (error) {
        this.checkoutError = 'Checkout could not be started. Please try again.';
        // eslint-disable-next-line no-console
        console.error('[checkout] failed', error, this.cart);
      } finally {
        this.submitting = false;
      }
    },

    // =========================================================================
    // PRICING HELPERS
    // =========================================================================

    // plan amount for a period, in dollars, expressed per month
    monthlyOf(plan, period) {
      if (!plan || !plan.prices[period]) return 0;
      const amount = plan.prices[period].amount / 100;
      return period === 'year' ? amount / 12 : amount;
    },

    savingPercent(plan) {
      if (!plan.prices.month || !plan.prices.year) return 0;
      const full = plan.prices.month.amount * 12;
      if (!full) return 0;
      return Math.round((1 - plan.prices.year.amount / full) * 100);
    },

    describe(option) {
      return option.description || this.copy.descriptions[option.id] || '';
    },

    money(value) {
      const negative = value < 0;
      const absolute = Math.abs(value);
      const fixed = (Math.round(absolute * 100) / 100)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `${negative ? '-$' : '$'}${fixed}`;
    },

    money0(value) {
      return `$${Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    },
  },
};
</script>

<!-- ============================================================================
     TYPEFACE — this page is typeset in Manrope (variable, 200–800).
     The font is NOT loaded here on purpose: a CSS @import is invisible to the
     browser's preload scanner and would sit behind this component's chunk.
     Self-host it once, app-wide:

         npm i @fontsource-variable/manrope

     then in src/main.js:

         import '@fontsource-variable/manrope';

     The stack below falls back to system-ui until that is in place, so the
     page stays legible either way — it just won't match the design.
     ========================================================================= -->

<style scoped lang="scss">
/* =============================================================================
   LOCAL DESIGN TOKENS
   Every value falls back to the Claude-design value, so the component looks
   correct even when colors_and_type.css is not loaded. If the app already
   defines these on :root they win, because the fallback only applies when the
   variable is undefined.
   ========================================================================== */
.builder-page {
  // Straight from the project's colors.scss (globally injected, no @import
  // needed). Each value is wrapped in interpolation because Sass does not
  // evaluate SassScript inside custom property values — without it the
  // variable name would be emitted as a literal string and ignored.
  --sb-accent: #{$color-primary-blue};
  --sb-accent-hover: #{$color-primary-dark-blue};
  --sb-accent-soft: #{$color-pastel-blue};
  --sb-ink: #{$color-gray-25};

  --sb-fg-primary: #{$global-text-color};
  --sb-fg-secondary: #{$color-gray-55};
  --sb-fg-tertiary: #{$color-gray-6C};

  --sb-border-default: #{$color-gray-E1};
  --sb-border-strong: #{$color-gray-C4};
  --sb-border-subtle: #{$color-gray-E9};

  --sb-success-bg: #{$color-pastel-green};
  --sb-success-fg: #{$color-primary-green};

  /* used for load/checkout failures, so red rather than orange */
  --sb-warn-bg: #{$color-white-peach};
  --sb-warn-fg: #{$color-primary-dark-red};

  --sb-surface: #{$color-white};
  --sb-bg-app: #{$color-gray-F5};
  --sb-bg-subtle: #{$color-gray-F5};

  --sb-shadow-xs: 0 1px 2px #{rgba($color-gray-25, 0.06)};
  --sb-shadow-sm: 0 6px 24px #{rgba($color-gray-25, 0.06)};

  --sb-dur: var(--dur-base, 160ms);
  --sb-ease: var(--ease-out, cubic-bezier(0.22, 0.61, 0.36, 1));

  /* Matches the design. 'Manrope Variable' is the family name @fontsource-variable
     registers; 'Manrope' covers a static/self-hosted copy. To hand the page back
     to the app's typeface instead, set both of these to `inherit`. */
  --sb-font-display: 'Manrope Variable', 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  --sb-font-body: 'Manrope Variable', 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;

  /* --- WEIGHT + TRACKING, tuned for the system stack -----------------------
     SF Pro / Segoe / Roboto have tighter apertures and narrower sidebearings
     than Manrope, so the design's 800 weight and -0.025em tracking close the
     letterforms up. These are dialled back one step.
     If Manrope is ever loaded app-wide, restore the design exactly by setting:
       --sb-w-display: 800;  --sb-w-strong: 700;
       --sb-track-display: -0.025em;  --sb-track-num: -0.02em;
     ---------------------------------------------------------------------- */
  --sb-w-display: 700;        /* headline, prices, card titles */
  --sb-w-strong: 600;         /* row names, subtotals, CTA */
  --sb-track-display: -0.016em;
  --sb-track-num: -0.012em;
  --sb-track-tight: -0.004em;
  --sb-track-caps: 0.1em;     /* uppercase micro-labels need MORE, not less */
}

*, *::before, *::after { box-sizing: border-box; }

/* ============ FULL-BLEED PAGE ============
   .builder-page paints the app background edge to edge; .wrap only constrains
   the content column. If a parent route container adds its own max-width or
   horizontal padding, uncomment the breakout rules below. */
.builder-page {
  width: 100%;
  min-height: 100%;
  background: var(--sb-bg-app);
  font-family: var(--sb-font-body);
  color: var(--sb-fg-primary);
  /* the original page set these on <body>; without them SF renders noticeably
     heavier on macOS, which fights the lighter weights below */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  /* --- breakout: escape a width-constrained / padded parent ---
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  */
}

.wrap {
  max-width: 1180px;
  margin: 0 auto;
  padding: 40px 32px 80px;
}
.wrap a { color: var(--sb-accent); text-decoration: none; }
.wrap a:hover { color: var(--sb-accent-hover); }
.wrap img { display: block; max-width: 100%; }

/* ============ HEAD ============ */
.builder-head { text-align: center; max-width: 720px; margin: 0 auto 12px; }
.builder-head .brand { display: flex; justify-content: center; margin-bottom: 22px; }
.builder-head .brand img { height: 26px; width: auto; }
.eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--sb-font-body); font-size: 11.5px; font-weight: var(--sb-w-strong);
  text-transform: uppercase; letter-spacing: var(--sb-track-caps); color: var(--sb-accent);
}
.eyebrow .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--sb-accent); }
.h-display {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display);
  font-size: clamp(28px, 3.4vw, 40px); line-height: 1.12;
  letter-spacing: var(--sb-track-display); margin: 14px 0 12px; color: var(--sb-ink);
}
.h-display .hl { color: var(--sb-accent); }
.lede { font-size: 17px; line-height: 1.55; color: var(--sb-fg-secondary); margin: 0; }

/* ============ STATE NOTES ============ */
.state-note {
  text-align: center; font-size: 14px; color: var(--sb-fg-secondary); margin: 48px 0;
}
.state-note.error { color: var(--sb-warn-fg); }
.state-note.small { text-align: left; font-size: 12.5px; margin: 10px 0 0; }

/* ============ BILLING TOGGLE ============ */
.billing-toggle {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 26px; padding: 5px;
  background: var(--sb-surface); border: 1px solid var(--sb-border-default);
  border-radius: 999px; box-shadow: var(--sb-shadow-xs);
}
.billing-opt {
  display: inline-flex; align-items: center; gap: 8px;
  height: 38px; padding: 0 20px; border: none; background: transparent;
  font-family: var(--sb-font-body); font-size: 14px; font-weight: var(--sb-w-strong);
  color: var(--sb-fg-secondary); cursor: pointer; border-radius: 999px;
  transition: color var(--sb-dur) var(--sb-ease), background var(--sb-dur) var(--sb-ease);
}
.billing-opt:hover:not(.active) { color: var(--sb-fg-primary); }
.billing-opt.active { background: var(--sb-ink); color: #fff; }
.billing-opt .save {
  font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
  padding: 2px 8px; border-radius: 999px;
  background: var(--sb-success-bg); color: var(--sb-success-fg);
}
.billing-opt.active .save { background: rgba($color-white, 0.18); color: #fff; }

/* ============ GRID ============ */
.builder-grid {
  display: grid; grid-template-columns: minmax(0, 1fr) 400px;
  gap: 28px; align-items: start; margin-top: 40px;
}

/* ============ STEP ============ */
.step { margin-bottom: 34px; }
.step-head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.step-num {
  width: 30px; height: 30px; flex: none; border-radius: 9px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--sb-ink); color: #fff;
  font-family: var(--sb-font-display); font-weight: var(--sb-w-strong); font-size: 13.5px;
}
.step-title {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 20px;
  letter-spacing: var(--sb-track-tight); margin: 0; color: var(--sb-ink);
}
.step-sub { font-size: 13.5px; color: var(--sb-fg-secondary); margin: 3px 0 0; }

/* ============ PLAN CARDS ============ */
/* padding-top leaves room for the badge, which sits 9px above the card edge */
.opt-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; padding-top: 9px; }
.opt {
  position: relative; text-align: left; cursor: pointer;
  background: var(--sb-surface); border: 1.5px solid var(--sb-border-default);
  border-radius: 14px; padding: 18px 18px 16px;
  transition: border-color var(--sb-dur) var(--sb-ease), box-shadow var(--sb-dur) var(--sb-ease);
}
.opt:hover { border-color: var(--sb-border-strong); }
.opt:focus-visible { outline: none; border-color: var(--sb-accent); box-shadow: 0 0 0 3px rgba($color-primary-blue, 0.24); }
.opt.selected { border-color: var(--sb-accent); box-shadow: 0 0 0 3px rgba($color-primary-blue, 0.14); }
.opt .tick {
  position: absolute; top: 14px; right: 14px;
  width: 20px; height: 20px; border-radius: 50%;
  border: 1.5px solid var(--sb-border-strong);
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; transition: background var(--sb-dur) var(--sb-ease), border-color var(--sb-dur) var(--sb-ease);
}
.opt .tick svg { width: 11px; height: 11px; opacity: 0; transition: opacity var(--sb-dur) var(--sb-ease); }
.opt.selected .tick { background: var(--sb-accent); border-color: var(--sb-accent); }
.opt.selected .tick svg { opacity: 1; }
.opt .badge {
  position: absolute; top: -9px; left: 16px; z-index: 1;
  font-family: var(--sb-font-body);
  font-size: 10px; font-weight: var(--sb-w-strong); letter-spacing: 0.08em;
  text-transform: uppercase; white-space: nowrap;
  color: #fff; background: var(--sb-accent); padding: 3px 9px; border-radius: 999px;
}
.opt .o-name {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-strong); font-size: 17px;
  letter-spacing: var(--sb-track-tight); margin: 0; color: var(--sb-ink);
}
.opt .o-price { display: flex; align-items: baseline; gap: 5px; margin: 8px 0 2px; }
.opt .o-price .amt {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 31px;
  letter-spacing: var(--sb-track-num); line-height: 1; color: var(--sb-ink);
  font-variant-numeric: tabular-nums;
}
.opt .o-price .per { font-size: 12px; font-weight: 500; color: var(--sb-fg-secondary); }
.opt .o-billed { font-size: 11.5px; color: var(--sb-fg-tertiary); min-height: 15px; margin-bottom: 8px; }
.opt .o-desc { font-size: 13px; line-height: 1.5; color: var(--sb-fg-secondary); margin: 0; }

/* onboarding row cards */
.opt-rows { display: flex; flex-direction: column; gap: 12px; }
.opt-row {
  position: relative; cursor: pointer; display: flex; align-items: center; gap: 16px;
  background: var(--sb-surface); border: 1.5px solid var(--sb-border-default);
  border-radius: 14px; padding: 16px 18px;
  transition: border-color var(--sb-dur) var(--sb-ease), box-shadow var(--sb-dur) var(--sb-ease);
}
.opt-row:hover { border-color: var(--sb-border-strong); }
.opt-row:focus-visible { outline: none; border-color: var(--sb-accent); box-shadow: 0 0 0 3px rgba($color-primary-blue, 0.24); }
.opt-row.selected { border-color: var(--sb-accent); box-shadow: 0 0 0 3px rgba($color-primary-blue, 0.14); }
.opt-row .tick {
  width: 20px; height: 20px; flex: none; border-radius: 50%;
  border: 1.5px solid var(--sb-border-strong);
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; transition: background var(--sb-dur) var(--sb-ease), border-color var(--sb-dur) var(--sb-ease);
}
.opt-row .tick svg { width: 11px; height: 11px; opacity: 0; transition: opacity var(--sb-dur) var(--sb-ease); }
.opt-row.selected .tick { background: var(--sb-accent); border-color: var(--sb-accent); }
.opt-row.selected .tick svg { opacity: 1; }
.opt-row .r-body { flex: 1; }
.opt-row .r-name {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-strong); font-size: 15.5px;
  letter-spacing: var(--sb-track-tight); margin: 0; color: var(--sb-ink);
}
.opt-row .r-desc { font-size: 13px; line-height: 1.5; color: var(--sb-fg-secondary); margin: 3px 0 0; }
.opt-row .r-price {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 18px;
  letter-spacing: var(--sb-track-num); white-space: nowrap; color: var(--sb-ink);
  font-variant-numeric: tabular-nums;
}
.opt-row .r-price .once {
  display: block; font-family: var(--sb-font-body); font-size: 10px; font-weight: 600;
  letter-spacing: 0.07em; text-transform: uppercase; color: var(--sb-fg-tertiary); text-align: right;
  margin-top: 2px;
}

/* ============ SUMMARY ============ */
.summary { position: sticky; top: 24px; }
.summary-card {
  background: var(--sb-surface); border: 1px solid var(--sb-border-default);
  border-radius: 18px; box-shadow: var(--sb-shadow-sm);
  overflow: hidden;
}
.summary-head { padding: 22px 24px 18px; border-bottom: 1px solid var(--sb-border-subtle); }
.summary-head h3 {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 18px;
  letter-spacing: var(--sb-track-tight); margin: 0; color: var(--sb-ink);
}
.summary-head p { font-size: 12.5px; color: var(--sb-fg-secondary); margin: 4px 0 0; }
.summary-body { padding: 8px 24px 4px; }

.sec-label {
  font-size: 10.5px; font-weight: var(--sb-w-strong); letter-spacing: var(--sb-track-caps);
  text-transform: uppercase;
  color: var(--sb-fg-tertiary); margin: 16px 0 4px;
}
.li {
  display: flex; align-items: baseline; justify-content: space-between;
  gap: 14px; padding: 8px 0; font-size: 14px;
}
.li .li-name { color: var(--sb-fg-primary); }
.li .li-name .li-sub { display: block; font-size: 11.5px; color: var(--sb-fg-tertiary); margin-top: 2px; }
.li .li-amt {
  font-weight: var(--sb-w-strong); color: var(--sb-fg-primary); white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.summary-divider { height: 1px; background: var(--sb-border-subtle); margin: 10px 0; }

.subtotal { display: flex; justify-content: space-between; align-items: baseline; padding: 8px 0; font-size: 14px; }
.subtotal .st-name { color: var(--sb-fg-secondary); font-weight: 500; }
.subtotal .st-amt {
  font-weight: var(--sb-w-strong); color: var(--sb-fg-primary);
  font-variant-numeric: tabular-nums;
}

.total-block { background: var(--sb-bg-subtle); margin: 12px -24px 0; padding: 20px 24px 22px; }
.total-row { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; }
.total-row .t-label {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-strong); font-size: 15px;
  letter-spacing: var(--sb-track-tight); color: var(--sb-ink);
}
.total-row .t-label small {
  display: block; font-family: var(--sb-font-body); font-weight: 500; font-size: 11.5px;
  color: var(--sb-fg-secondary); margin-top: 2px;
}
.total-row .t-amt {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 31px;
  letter-spacing: var(--sb-track-num); line-height: 1; color: var(--sb-ink);
  font-variant-numeric: tabular-nums;
}
.total-row .t-amt .per {
  font-family: var(--sb-font-body); font-size: 13px; font-weight: 500; color: var(--sb-fg-secondary);
}
.annual-note { margin-top: 8px; font-size: 12.5px; color: var(--sb-fg-secondary); }
.annual-note .save { color: var(--sb-success-fg); font-weight: 700; }
.paynow-row {
  margin-top: 14px; padding-top: 14px;
  border-top: 1px solid var(--sb-border-default);
  display: flex; align-items: flex-end; justify-content: space-between; gap: 12px;
}
.paynow-row .pn-label {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-strong); font-size: 14px;
  letter-spacing: var(--sb-track-tight); color: var(--sb-ink);
}
.paynow-row .pn-label small {
  display: block; font-family: var(--sb-font-body); font-weight: 500; font-size: 11.5px;
  color: var(--sb-fg-secondary); margin-top: 2px;
}
.paynow-row .pn-amt {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 20px;
  letter-spacing: var(--sb-track-num); line-height: 1; color: var(--sb-ink);
  font-variant-numeric: tabular-nums;
}

.summary-foot { padding: 20px 24px 24px; }
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; height: 50px; padding: 0 24px;
  border-radius: 11px; border: 1px solid transparent;
  font-family: var(--sb-font-body); font-weight: var(--sb-w-strong); font-size: 15px;
  letter-spacing: 0.005em;
  cursor: pointer;
  transition: background var(--sb-dur) var(--sb-ease), box-shadow var(--sb-dur) var(--sb-ease), transform var(--sb-dur) var(--sb-ease);
}
.btn .btn-arrow { width: 17px; height: 17px; flex: none; }
.btn[disabled] { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: var(--sb-accent); color: #fff; }
.btn-primary:hover:not([disabled]) {
  background: var(--sb-accent-hover); transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba($color-primary-blue, 0.3);
}
.trust { margin-top: 14px; display: flex; flex-direction: column; gap: 8px; }
.trust span { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--sb-fg-secondary); }
.trust .check {
  width: 16px; height: 16px; border-radius: 50%; flex: none;
  background: var(--sb-success-bg); color: var(--sb-success-fg);
  display: inline-flex; align-items: center; justify-content: center;
}
.trust .check svg { width: 10px; height: 10px; }

/* ============ RESPONSIVE ============ */
@media (max-width: 940px) {
  .builder-grid { grid-template-columns: 1fr; }
  .summary { position: static; }
  .summary-card { margin-top: 8px; }
}
@media (max-width: 560px) {
  .wrap { padding: 28px 18px 64px; }
  .opt-grid { grid-template-columns: 1fr; }
}
</style>
